import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Ticket } from 'lucide-react';

export default function GananatyaDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
    window.addEventListener('locationChanged', fetchData);
    return () => window.removeEventListener('locationChanged', fetchData);
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const dist_id = localStorage.getItem('selected_dist_id');
      const distQuery = dist_id ? `?dist_id=${dist_id}` : '';

      const prodRes = await fetch(`/api/get_product_by_category/${id}${distQuery}`, {
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123',
          'Accept': 'application/json'
        }
      });
      const prodData = await prodRes.json();
      if (prodData.data && prodData.data.length > 0) {
        setProducts(prodData.data);
        setCategory({
          cat_name: prodData.data[0].cat_name,
          cat_image: prodData.data[0].image
        });
      } else {
        setProducts([]);
        setCategory({ cat_name: 'Category', cat_image: '' });
      }
    } catch (err) {
      console.error('Error fetching details:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '11:00 PM';
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
    const [h, m] = timeStr.split(':');
    if (h && m) {
      const hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const formattedHour = hour % 12 || 12;
      return `${formattedHour}:${m} ${ampm}`;
    }
    return timeStr;
  };

  if (loading) {
    return (
      <div className="bg-[#fcfcfc] min-h-screen pb-20 animate-pulse">
        <div className="relative w-full h-[320px] bg-gray-800 overflow-hidden flex items-end pb-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex items-center gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-gray-700 shadow-xl shrink-0"></div>
            <div className="flex-1">
              <div className="h-8 bg-gray-700 w-64 rounded mb-2"></div>
              <div className="h-4 bg-gray-700 w-48 rounded mb-4"></div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <div className="h-8 bg-gray-200 w-48 rounded mb-6"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl h-[280px] border border-gray-100 shadow-xs"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !category) {
    return <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center text-red-500 font-bold">Failed to load details: {error}</div>;
  }

  return (
    <div className="bg-[#fcfcfc] min-h-screen pb-20">
      {/* Hero Banner */}
      <div className="relative w-full h-[300px] sm:h-[350px] bg-[#17052c] overflow-hidden flex items-end pb-8">
        <div className="absolute inset-0 z-0">
          <img
            src={category.cat_image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"}
            alt={category.cat_name}
            className="w-full h-full object-cover opacity-35 blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#17052c] via-[#17052c]/80 to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10 flex flex-col md:flex-row items-center md:items-end gap-6">
          <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden border-4 border-white shadow-2xl shrink-0 bg-white">
            <img
              src={category.cat_image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"}
              alt={category.cat_name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-white text-center md:text-left flex-1 pb-1">
            <span className="bg-red-600/30 border border-red-500/40 text-red-300 font-extrabold text-[10px] sm:text-xs tracking-widest uppercase px-3 py-1 rounded-full mb-2.5 inline-block backdrop-blur-md">
              EXCLUSIVE GANANATYA PARTY
            </span>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-white tracking-tight mb-2 drop-shadow-md">{category.cat_name}</h1>
            <p className="text-gray-200 text-xs sm:text-sm font-medium max-w-2xl leading-relaxed">
              Experience the legendary stage performances of {category.cat_name}. Book your tickets for upcoming shows below.
            </p>
          </div>
        </div>
      </div>

      {/* Shows List */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex items-center mb-6 px-1 sm:px-0">
          <div className="h-5 w-1 bg-red-600 rounded-full mr-2.5"></div>
          <h2 className="text-lg md:text-2xl font-bold text-gray-900"> Shows</h2>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-xs border border-gray-100">
            <div className="text-5xl mb-4 opacity-50">🎭</div>
            <h3 className="text-lg font-bold text-gray-700">No upcoming shows</h3>
            <p className="text-gray-400 mt-1 text-xs">Check back later for new performance schedules.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((show) => {
              const dateObj = show.jatra_date ? new Date(show.jatra_date) : null;
              const formattedDate = dateObj
                ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                : 'TBA';

              const showTitle = show.jatra_name || 'Jatra Show';
              const locationText = show.jatra_location || show.district_name || 'Odisha';
              const showId = show.product_id || show.id;

              return (
                <div
                  onClick={() => navigate(`/shows/${showId}`)}
                  key={showId}
                  className="bg-white rounded-2xl border border-gray-100 p-3 shadow-xs hover:shadow-md transition-all duration-300 group cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    {/* Image Container */}
                    <div className="relative h-44 sm:h-52 bg-gray-100 w-full rounded-xl overflow-hidden">
                      <img
                        src={show.image || "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg"}
                        alt={showTitle}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.src = "https://upload.wikimedia.org/wikipedia/commons/4/41/Placeholder_view_vector.svg" }}
                      />

                      {/* Top Left Party Pill */}
                      <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[9px] font-bold px-2 py-0.5 rounded-md truncate max-w-[120px] z-10">
                        {show.cat_name || category.cat_name}
                      </div>

                      {/* Top Right Price Pill */}
                      {show.ticket_price && (
                        <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-md text-gray-900 text-[10px] font-extrabold px-2 py-0.5 rounded-md shadow-2xs z-10">
                          ₹{show.ticket_price}
                        </div>
                      )}

                      {/* Hover Ticket Button Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-3 pointer-events-none z-20">
                        <div className="transform translate-y-3 group-hover:translate-y-0 transition-transform duration-300 w-full flex justify-center">
                          <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white font-normal text-xs py-1.5 px-3.5 rounded-md flex items-center justify-center gap-1.5 shadow-md border border-white/20">
                            <Ticket size={13} />
                            <span>Book Tickets</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Show Title & Aligned Info */}
                    <div className="mt-2.5 min-w-0">
                      <h3 className="text-xs sm:text-sm font-bold text-gray-900 truncate group-hover:text-red-600 transition-colors" title={showTitle}>
                        {showTitle}
                      </h3>

                      <div className="mt-2 space-y-1 min-w-0">
                        <p className="text-[11px] sm:text-xs text-gray-600 flex items-center gap-1.5 font-medium min-w-0">
                          <Calendar size={12} className="text-red-500 shrink-0" />
                          <span className="truncate">{formattedDate} • {formatTime(show.jatra_time || show.time)}</span>
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-600 flex items-center gap-1.5 font-medium min-w-0" title={locationText}>
                          <MapPin size={12} className="text-blue-500 shrink-0" />
                          <span className="truncate block">{locationText}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Book Button */}
                  <button className="w-full bg-red-50 hover:bg-red-600 text-red-600 hover:text-white text-center text-xs font-bold py-1.5 rounded-lg transition-colors mt-3">
                    Book Tickets
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
